import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  BookOpen, ChevronRight, ChevronDown, Search, Send, Lock, Crown,
  MessageSquare, Sparkles, ArrowLeft, Loader2, GraduationCap, Globe, ExternalLink,
  CheckCircle2, X, Wand2, Flame, Trophy, Target, TrendingUp, Play,
  CircleCheck, CircleX, CalendarClock, ArrowRight, Highlighter, StickyNote, Trash2
} from 'lucide-react';
import API from '../utils/api';

// Highlight query matches (case-insensitive) in a safe way
const escapeHtml = (s) => s.replace(/[&<>"']/g, c => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[c]));
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const highlightMatch = (text, query) => {
  const safe = escapeHtml(text || '');
  const q = (query || '').trim();
  if (!q || q.length < 2) return safe;
  return safe.replace(
    new RegExp(`(${escapeRegex(escapeHtml(q))})`, 'gi'),
    '<mark class="bg-emerald-500/30 text-emerald-200 rounded px-0.5">$1</mark>'
  );
};

// Color mapping for highlights
const HL_CLASS = {
  yellow: 'bg-yellow-400/25 text-yellow-100 border-b-2 border-yellow-400/50',
  emerald: 'bg-emerald-500/20 text-emerald-100 border-b-2 border-emerald-400/50',
  rose: 'bg-rose-500/20 text-rose-100 border-b-2 border-rose-400/50',
};

// Render a paragraph with any matching highlights wrapped as <mark>
// highlights: [{highlight_id, text, color, note}]
const renderParagraphWithHighlights = (text, highlights, onHlClick) => {
  if (!highlights || highlights.length === 0) return text;
  // Sort by text length desc so longer spans win
  const sorted = [...highlights].sort((a, b) => (b.text?.length || 0) - (a.text?.length || 0));
  // Build a list of [start, end, hl] ranges
  const ranges = [];
  for (const hl of sorted) {
    if (!hl.text) continue;
    const idx = text.indexOf(hl.text);
    if (idx === -1) continue;
    const end = idx + hl.text.length;
    // avoid overlap with already-claimed ranges
    if (ranges.some(r => !(end <= r.start || idx >= r.end))) continue;
    ranges.push({ start: idx, end, hl });
  }
  if (ranges.length === 0) return text;
  ranges.sort((a, b) => a.start - b.start);
  const out = [];
  let cursor = 0;
  ranges.forEach((r, i) => {
    if (r.start > cursor) out.push(text.slice(cursor, r.start));
    const cls = HL_CLASS[r.hl.color] || HL_CLASS.yellow;
    out.push(
      <mark
        key={r.hl.highlight_id}
        data-testid={`kb-highlight-${r.hl.highlight_id}`}
        onClick={(e) => { e.stopPropagation(); onHlClick?.(r.hl); }}
        className={`${cls} cursor-pointer rounded px-0.5 hover:brightness-125 transition`}
        title={r.hl.note ? `📝 ${r.hl.note}` : 'Klick für Optionen'}
      >
        {text.slice(r.start, r.end)}
        {r.hl.note && <StickyNoteInline />}
      </mark>
    );
    cursor = r.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return <>{out}</>;
};

// Tiny inline note indicator
const StickyNoteInline = () => (
  <sup className="ml-0.5 inline-block text-[10px]" aria-hidden>📝</sup>
);

const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const [structure, setStructure] = useState([]);
  const [expandedParts, setExpandedParts] = useState({});
  const [expandedChapters, setExpandedChapters] = useState({});
  const [readTopics, setReadTopics] = useState(new Set());
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicContent, setTopicContent] = useState(null);
  const [loadingTopic, setLoadingTopic] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const chatEndRef = useRef(null);

  // Progress Dashboard
  const [progress, setProgress] = useState(null);

  // KB Access / Pre-Launch
  const [kbStatus, setKbStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Quiz
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);

  // Highlights & Notes
  const [highlights, setHighlights] = useState([]);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteEditingHl, setNoteEditingHl] = useState(null); // existing highlight being edited
  const [notePendingText, setNotePendingText] = useState(''); // text pending highlight (selection)
  const [noteDraft, setNoteDraft] = useState('');
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);

  // Text-Selektion → KI-Erklärung
  const [selectionInfo, setSelectionInfo] = useState(null); // { text, x, y }
  const [explainOpen, setExplainOpen] = useState(false);
  const [explainQuery, setExplainQuery] = useState('');
  const [explainResult, setExplainResult] = useState('');
  const [explainLoading, setExplainLoading] = useState(false);
  const topicContentRef = useRef(null);

  const handleTextSelection = useCallback(() => {
    const sel = window.getSelection?.();
    const text = sel?.toString()?.trim() || '';
    if (!text || text.length < 3 || text.length > 1500) {
      setSelectionInfo(null);
      return;
    }
    // Prüfen: Selektion muss innerhalb des Topic-Content liegen
    if (!topicContentRef.current || !sel.rangeCount) {
      setSelectionInfo(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!topicContentRef.current.contains(range.commonAncestorContainer)) {
      setSelectionInfo(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    setSelectionInfo({
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
  }, []);

  const requestExplanation = async (customText) => {
    const passage = customText ?? selectionInfo?.text;
    if (!passage) return;
    setExplainQuery(passage);
    setSelectionInfo(null);
    setExplainOpen(true);
    setExplainResult('');
    setExplainLoading(true);
    try {
      const res = await fetch(`${API}/knowledge/explain`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passage,
          topic_title: topicContent?.title || '',
          topic_context: (topicContent?.content || '').slice(0, 2000),
        })
      });
      const data = await res.json();
      if (res.ok) {
        setExplainResult(data.explanation);
      } else {
        setExplainResult(`Fehler: ${data.detail || 'Unbekannter Fehler'}`);
      }
    } catch (e) {
      setExplainResult('Verbindungsfehler. Bitte erneut versuchen.');
    }
    setExplainLoading(false);
  };

  useEffect(() => {
    fetchStructure();
    checkPremium();
    fetchReads();
    fetchProgress();
    fetchKbStatus();
  }, []);

  const fetchKbStatus = async () => {
    try {
      const res = await fetch(`${API}/knowledge/status`, { credentials: 'include' });
      if (res.ok) setKbStatus(await res.json());
    } catch (e) {}
  };

  const isTeaser = kbStatus?.access_level === 'teaser';
  const canUseFullKB = kbStatus && (kbStatus.access_level === 'admin' || kbStatus.access_level === 'full');
  // Admin override: wenn Backend sagt "admin", dann gilt Premium — unabhängig von /auth/me
  const effectiveIsPremium = isPremium || kbStatus?.is_admin === true || kbStatus?.access_level === 'admin' || kbStatus?.access_level === 'full';
  const isAdmin = kbStatus?.is_admin === true || kbStatus?.access_level === 'admin';

  const fetchProgress = async () => {
    try {
      const res = await fetch(`${API}/knowledge/progress`, { credentials: 'include' });
      if (res.ok) setProgress(await res.json());
    } catch (e) {}
  };

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const checkPremium = async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setIsPremium(data.is_admin === true || data.subscription_plan === 'premium' || data.subscription_status === 'lifetime');
      }
    } catch (e) {}
  };

  const fetchStructure = async () => {
    try {
      const res = await fetch(`${API}/knowledge/structure`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStructure(data.structure);
      } else if (res.status === 401) {
        setLoadError('Bitte melde dich erneut an — deine Session ist abgelaufen.');
      } else {
        setLoadError(`Fehler beim Laden der Wissensdatenbank (HTTP ${res.status}).`);
      }
    } catch (e) {
      setLoadError('Verbindung zum Server fehlgeschlagen. Prüfe deine Internetverbindung.');
    }
  };

  const fetchReads = async () => {
    try {
      const res = await fetch(`${API}/knowledge/reads`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setReadTopics(new Set(data.topic_ids || []));
      }
    } catch (e) {}
  };

  const markRead = async (topicId) => {
    if (readTopics.has(topicId)) return;
    setReadTopics(prev => new Set(prev).add(topicId));
    try {
      await fetch(`${API}/knowledge/topic/${topicId}/read`, { method: 'POST', credentials: 'include' });
      fetchProgress();
    } catch (e) {}
  };

  const toggleRead = async (topicId, e) => {
    e?.stopPropagation();
    const isRead = readTopics.has(topicId);
    setReadTopics(prev => {
      const s = new Set(prev);
      if (isRead) s.delete(topicId); else s.add(topicId);
      return s;
    });
    try {
      await fetch(`${API}/knowledge/topic/${topicId}/read`, {
        method: isRead ? 'DELETE' : 'POST',
        credentials: 'include'
      });
      fetchProgress();
    } catch (e) {}
  };

  // ==== QUIZ ====
  const openQuiz = async () => {
    if (!topicContent?.topic_id || !canUseFullKB) return;
    setQuizOpen(true);
    setQuizLoading(true);
    setQuizData(null);
    setQuizAnswers({});
    setQuizResult(null);
    try {
      const res = await fetch(`${API}/knowledge/quiz/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic_id: topicContent.topic_id })
      });
      const data = await res.json();
      if (res.ok) {
        setQuizData(data);
      } else {
        setQuizData({ error: data.detail || 'Quiz konnte nicht erzeugt werden.' });
      }
    } catch (e) {
      setQuizData({ error: 'Verbindungsfehler.' });
    }
    setQuizLoading(false);
  };

  const submitQuiz = async () => {
    if (!quizData?.quiz_id) return;
    setQuizSubmitting(true);
    try {
      const res = await fetch(`${API}/knowledge/quiz/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quiz_id: quizData.quiz_id, answers: quizAnswers })
      });
      const data = await res.json();
      if (res.ok) {
        setQuizResult(data);
        if (data.passed) {
          setReadTopics(prev => new Set(prev).add(topicContent.topic_id));
          fetchProgress();
        }
      }
    } catch (e) {}
    setQuizSubmitting(false);
  };

  const retryQuiz = () => {
    setQuizData(null);
    setQuizAnswers({});
    setQuizResult(null);
    openQuiz();
  };

  const loadTopic = async (topicId) => {
    setLoadingTopic(true);
    setSelectedTopic(topicId);
    setHighlights([]);
    try {
      const res = await fetch(`${API}/knowledge/topic/${topicId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTopicContent(data);
        if (canUseFullKB && !data.preview) {
          markRead(topicId);
          loadHighlights(topicId);
        }
      }
    } catch (e) {}
    setLoadingTopic(false);
  };

  const loadHighlights = async (topicId) => {
    try {
      const res = await fetch(`${API}/knowledge/topic/${topicId}/highlights`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHighlights(data.highlights || []);
      }
    } catch (e) {}
  };

  const createHighlight = async (text, color = 'yellow', note = '') => {
    if (!topicContent?.topic_id) return null;
    try {
      const res = await fetch(`${API}/knowledge/topic/${topicContent.topic_id}/highlights`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, color, note })
      });
      if (res.ok) {
        const hl = await res.json();
        setHighlights(prev => [...prev, hl]);
        return hl;
      }
    } catch (e) {}
    return null;
  };

  const updateHighlight = async (highlightId, patch) => {
    try {
      const res = await fetch(`${API}/knowledge/highlight/${highlightId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      if (res.ok) {
        setHighlights(prev => prev.map(h => h.highlight_id === highlightId ? { ...h, ...patch } : h));
      }
    } catch (e) {}
  };

  const deleteHighlight = async (highlightId) => {
    try {
      await fetch(`${API}/knowledge/highlight/${highlightId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setHighlights(prev => prev.filter(h => h.highlight_id !== highlightId));
    } catch (e) {}
  };

  const openNoteFromSelection = () => {
    if (!selectionInfo?.text) return;
    setNotePendingText(selectionInfo.text);
    setNoteEditingHl(null);
    setNoteDraft('');
    setNoteModalOpen(true);
    setSelectionInfo(null);
  };

  const openNoteEditor = (hl) => {
    setNoteEditingHl(hl);
    setNotePendingText('');
    setNoteDraft(hl.note || '');
    setNoteModalOpen(true);
  };

  const saveNote = async () => {
    const note = noteDraft.trim();
    if (noteEditingHl) {
      await updateHighlight(noteEditingHl.highlight_id, { note });
    } else if (notePendingText) {
      await createHighlight(notePendingText, 'yellow', note);
    }
    setNoteModalOpen(false);
    setNoteEditingHl(null);
    setNotePendingText('');
    setNoteDraft('');
  };

  const highlightFromSelection = async (color = 'yellow') => {
    if (!selectionInfo?.text) return;
    await createHighlight(selectionInfo.text, color, '');
    setSelectionInfo(null);
    window.getSelection?.()?.removeAllRanges();
  };

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/knowledge/search`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery })
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      }
    } catch (e) {}
    setSearching(false);
  }, [searchQuery]);

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const endpoint = webSearch ? `${API}/knowledge/research` : `${API}/knowledge/chat`;
      const body = webSearch
        ? { message: msg, session_id: 'kb_main', recency: 'month' }
        : { message: msg, session_id: 'kb_main' };
      const res = await fetch(endpoint, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, {
          role: 'ai',
          text: data.response,
          sources: data.sources_used,
          citations: data.citations || [],
          sourceList: data.sources || [],
          provider: data.provider || 'gpt',
        }]);
      } else {
        const err = await res.json();
        setChatMessages(prev => [...prev, { role: 'ai', text: `Fehler: ${err.detail || 'Unbekannter Fehler'}` }]);
      }
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'ai', text: 'Verbindungsfehler. Bitte erneut versuchen.' }]);
    }
    setChatLoading(false);
  };

  const togglePart = (partId) => {
    // Accordion: nur ein Part gleichzeitig offen. Gleicher Klick = schließen.
    setExpandedParts(prev => (prev.id === partId ? {} : { id: partId }));
    setExpandedChapters({}); // andere Chapters schließen beim Part-Wechsel
  };

  const toggleChapter = (chapterId) => {
    // Accordion: nur ein Chapter gleichzeitig offen innerhalb des aktiven Parts.
    setExpandedChapters(prev => (prev.id === chapterId ? {} : { id: chapterId }));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="knowledge-base-page">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-emerald-400" />
              <h1 className="text-xl font-bold">Wissensdatenbank</h1>
              {isTeaser && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] font-semibold text-emerald-300 uppercase tracking-wider">
                  <Sparkles className="h-3 w-3" />
                  Vorschau
                </span>
              )}
              {isAdmin && (
                <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-semibold text-amber-300 uppercase tracking-wider">
                  Admin
                </span>
              )}
            </div>
          </div>
          <Button
            data-testid="ai-chat-toggle"
            onClick={() => canUseFullKB && setShowChat(!showChat)}
            disabled={isTeaser}
            title={isTeaser ? 'Ab 01.05.2026 verfügbar' : ''}
            variant={showChat ? "default" : "outline"}
            className={`${showChat ? "bg-emerald-600 hover:bg-emerald-700" : "border-emerald-500/30 text-emerald-400"} ${isTeaser ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {isTeaser ? <Lock className="h-4 w-4 mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            KI-Mentor
          </Button>
        </div>
        {/* Pre-launch banner */}
        {isTeaser && (
          <div
            className="bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent border-t border-emerald-500/20"
            data-testid="kb-prelaunch-banner"
          >
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs">
                <Lock className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-zinc-300">
                  <strong className="text-emerald-300">Launch am 01.05.2026</strong>
                  {' '}· Erkunde schon jetzt die Struktur — voller Zugang inkl. KI-Mentor & Quiz ab Launch.
                </span>
              </div>
              {kbStatus?.seconds_to_launch > 0 && (
                <span className="text-[10px] font-semibold text-emerald-300 tabular-nums">
                  noch {Math.ceil(kbStatus.seconds_to_launch / 86400)} Tage
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="max-w-[1600px] mx-auto flex h-[calc(100vh-57px)]">
        {/* Sidebar - Categories */}
        <aside className="w-80 border-r border-zinc-800 flex flex-col">
          {/* Search */}
          <div className="p-3 border-b border-zinc-800">
            <div className="relative">
              {searching ? (
                <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 animate-spin" />
              ) : (
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              )}
              <Input
                data-testid="kb-search-input"
                placeholder="KI-Suche: Begriff eingeben + Enter…"
                value={searchQuery}
                onChange={e => {
                  const v = e.target.value;
                  setSearchQuery(v);
                  if (!v.trim()) setSearchResults([]);
                }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="pl-9 pr-9 bg-zinc-800/50 border-zinc-700"
              />
              {searchQuery && (
                <button
                  data-testid="kb-search-clear"
                  onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-200 hover:bg-white/5"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <ScrollArea className="flex-1">
            {searching && searchResults.length === 0 ? (
              <div className="p-4 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-emerald-400 animate-pulse" />
                KI durchsucht die Wissensdatenbank…
              </div>
            ) : searchResults.length > 0 ? (
              <div className="p-3 space-y-2" data-testid="kb-search-results">
                <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold px-1 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" />
                  {searchResults.length} relevante Treffer
                </p>
                {searchResults.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => loadTopic(r.topic_id)}
                    data-testid={`kb-search-result-${i}`}
                    className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                      selectedTopic === r.topic_id
                        ? 'bg-emerald-500/10 border-emerald-500/40'
                        : 'bg-zinc-900/50 border-zinc-800 hover:border-emerald-500/30 hover:bg-zinc-800/50'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-[10px] font-bold text-emerald-400 shrink-0 mt-0.5 tabular-nums">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-100 leading-tight"
                          dangerouslySetInnerHTML={{ __html: highlightMatch(r.title, searchQuery) }}
                        />
                        <p className="text-[10px] text-zinc-500 truncate mt-0.5">
                          {r.part_title} · {r.chapter_title}
                        </p>
                        {r.reason && (
                          <p className="text-[11px] text-emerald-300/80 italic mt-1 leading-snug line-clamp-2">
                            „{r.reason}"
                          </p>
                        )}
                      </div>
                      {readTopics.has(r.topic_id) && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : searchQuery && !searching ? (
              <div className="p-6 text-center text-xs text-zinc-500">
                Keine passenden Themen gefunden. Versuche es mit einem anderen Begriff.
              </div>
            ) : (
              <div className="p-3 space-y-1">
                {structure.map(part => {
                  const partOpen = expandedParts.id === part.id;
                  const totalTopics = part.chapters.reduce((n, c) => n + c.topics.length, 0);
                  const readCount = part.chapters.reduce(
                    (n, c) => n + c.topics.filter(t => readTopics.has(t.topic_id)).length, 0
                  );
                  return (
                  <div key={part.id}>
                    <button onClick={() => togglePart(part.id)}
                      data-testid={`kb-part-${part.id}`}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-zinc-800/50 transition-colors text-left">
                      {partOpen ? <ChevronDown className="h-4 w-4 text-emerald-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
                      <BookOpen className="h-4 w-4 text-emerald-400 shrink-0" />
                      <span className="text-sm font-medium truncate flex-1 min-w-0">{part.title}</span>
                      {totalTopics > 0 && (
                        <span className={`text-[10px] shrink-0 tabular-nums pl-1 ${readCount === totalTopics ? 'text-emerald-400 font-semibold' : 'text-zinc-500'}`}>
                          {readCount}/{totalTopics}
                        </span>
                      )}
                    </button>
                    {partOpen && (
                      <div className="ml-4 border-l border-zinc-800 pl-2 space-y-0.5">
                        {part.chapters.map(ch => {
                          const chapKey = `${part.id}_${ch.id}`;
                          const chapOpen = expandedChapters.id === chapKey;
                          return (
                          <div key={ch.id}>
                            <button onClick={() => toggleChapter(chapKey)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/30 text-left">
                              {chapOpen ? <ChevronDown className="h-3 w-3 text-zinc-400 shrink-0" /> : <ChevronRight className="h-3 w-3 text-zinc-500 shrink-0" />}
                              <span className="text-xs font-medium text-zinc-300 truncate">{ch.title}</span>
                            </button>
                            {chapOpen && (
                              <div className="ml-5 space-y-0.5 py-1">
                                {ch.topics.map(t => {
                                  const isRead = readTopics.has(t.topic_id);
                                  return (
                                  <div key={t.topic_id} className="flex items-center gap-0.5 group">
                                    <button
                                      onClick={() => loadTopic(t.topic_id)}
                                      data-testid={`kb-topic-${t.topic_id}`}
                                      className={`flex-1 text-left px-2 py-1 rounded text-xs transition-colors flex items-center gap-1.5 ${
                                        selectedTopic === t.topic_id ? 'bg-emerald-500/10 text-emerald-400' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/30'
                                      } ${!t.has_content ? 'opacity-50' : ''}`}
                                    >
                                      {!canUseFullKB && <Lock className="h-3 w-3 shrink-0" />}
                                      <span className="truncate flex-1">{t.title}</span>
                                    </button>
                                    {canUseFullKB && (
                                      <button
                                        onClick={(e) => toggleRead(t.topic_id, e)}
                                        title={isRead ? 'Als ungelesen markieren' : 'Als gelesen markieren'}
                                        data-testid={`kb-read-toggle-${t.topic_id}`}
                                        className={`shrink-0 p-1 rounded transition-all ${
                                          isRead
                                            ? 'text-emerald-400 opacity-100'
                                            : 'text-zinc-600 opacity-0 group-hover:opacity-100 hover:text-zinc-300'
                                        }`}
                                      >
                                        <CheckCircle2 className={`h-3.5 w-3.5 ${isRead ? 'fill-emerald-500/20' : ''}`} />
                                      </button>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex">
          <div className={`flex-1 overflow-y-auto ${showChat ? 'border-r border-zinc-800' : ''}`}>
            {kbStatus?.is_launched && !effectiveIsPremium && !isAdmin ? (
              /* Post-Launch Premium Gate */
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center max-w-md space-y-6">
                  <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center">
                    <Crown className="h-10 w-10 text-amber-400" />
                  </div>
                  <h2 className="text-2xl font-bold">Premium Feature</h2>
                  <p className="text-muted-foreground">
                    Die KI-Wissensdatenbank ist exklusiv für Premium-Mitglieder verfügbar.
                    Lerne Trading-Konzepte mit einem persönlichen KI-Mentor und Zugang zu unserer
                    umfassenden Wissensbibliothek.
                  </p>
                  <Button onClick={() => navigate('/settings')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                    <Crown className="h-4 w-4 mr-2" />
                    Upgrade auf Premium
                  </Button>
                </div>
              </div>
            ) : loadingTopic ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
              </div>
            ) : topicContent ? (
              /* Topic Content */
              <div className="p-8 max-w-3xl">
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-xs text-emerald-400 font-medium mb-1">{topicContent.part_title} / {topicContent.chapter_title}</p>
                    <h2 className="text-2xl font-bold">{topicContent.title}</h2>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {canUseFullKB && topicContent.content && topicContent.content.length > 100 && (
                      <Button
                        data-testid="kb-quiz-open-btn"
                        variant="outline"
                        size="sm"
                        onClick={openQuiz}
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      >
                        <Target className="h-4 w-4 mr-2" />
                        Verständnis prüfen
                      </Button>
                    )}
                    {canUseFullKB && (
                      <Button
                        data-testid="kb-notes-panel-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => setNotesPanelOpen(true)}
                        title="Meine Notizen & Highlights"
                        className="border-zinc-700 text-zinc-400 hover:text-yellow-300 hover:border-yellow-500/40"
                      >
                        <Highlighter className="h-4 w-4 mr-2" />
                        Notizen
                        {highlights.length > 0 && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 text-[10px] font-bold">
                            {highlights.length}
                          </span>
                        )}
                      </Button>
                    )}
                    {canUseFullKB && (
                      <Button
                        data-testid="kb-topic-read-toggle"
                        variant={readTopics.has(topicContent.topic_id) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleRead(topicContent.topic_id)}
                        className={readTopics.has(topicContent.topic_id)
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'border-zinc-700 text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/40'}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        {readTopics.has(topicContent.topic_id) ? 'Gelesen' : 'Als gelesen markieren'}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <div
                    ref={topicContentRef}
                    onMouseUp={canUseFullKB ? handleTextSelection : undefined}
                    onTouchEnd={canUseFullKB ? handleTextSelection : undefined}
                    className={`prose prose-invert prose-sm max-w-none relative ${topicContent.preview ? 'select-none' : ''}`}
                    data-testid="kb-topic-content"
                  >
                  {topicContent.content ? (
                    topicContent.content.split('\n').map((p, i) => (
                      p.startsWith('**') ? (
                        <h3 key={i} className="text-lg font-semibold text-zinc-200 mt-4 mb-2">{p.replace(/\*\*/g, '')}</h3>
                      ) : p.startsWith('- ') ? (
                        <li key={i} className="text-zinc-300 ml-4">
                          {renderParagraphWithHighlights(p.substring(2), highlights, openNoteEditor)}
                        </li>
                      ) : p.trim() ? (
                        <p key={i} className="text-zinc-300 leading-relaxed mb-3">
                          {renderParagraphWithHighlights(p, highlights, openNoteEditor)}
                        </p>
                      ) : null
                    ))
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Dieser Inhalt wird gerade erstellt.</p>
                      <p className="text-xs mt-1">Frag den KI-Mentor zu diesem Thema!</p>
                    </div>
                  )}
                  </div>
                  {topicContent.preview && (
                    <>
                      {/* Blur-Fade + Fake-Content für Tiefe */}
                      <div aria-hidden className="pointer-events-none select-none mt-1 blur-[5px] opacity-50 space-y-2">
                        <div className="h-4 bg-zinc-800/80 rounded w-full" />
                        <div className="h-4 bg-zinc-800/80 rounded w-11/12" />
                        <div className="h-4 bg-zinc-800/80 rounded w-10/12" />
                        <div className="h-4 bg-zinc-800/80 rounded w-9/12" />
                        <div className="h-4 bg-zinc-800/80 rounded w-11/12" />
                        <div className="h-4 bg-zinc-800/80 rounded w-8/12" />
                        <div className="h-4 bg-zinc-800/80 rounded w-10/12" />
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-background via-background/95 to-transparent pointer-events-none" />
                      <div
                        className="absolute inset-x-0 bottom-6 flex justify-center"
                        data-testid="kb-topic-teaser-cta"
                      >
                        <div className="px-5 py-4 rounded-xl bg-zinc-950 border border-emerald-500/30 shadow-2xl shadow-emerald-500/10 text-center max-w-sm">
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <Lock className="h-4 w-4 text-emerald-400" />
                            <span className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Vorschau</span>
                          </div>
                          <p className="text-sm font-semibold text-zinc-100">
                            Vollständiger Inhalt ab <span className="text-emerald-300">01.05.2026</span>
                          </p>
                          <p className="text-[11px] text-zinc-500 mt-1">
                            Du wirst per E-Mail benachrichtigt, sobald es losgeht.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* Learning Dashboard (Welcome State) */
              <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="kb-learning-dashboard">
                {/* Hero */}
                <div className="relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900 p-6 lg:p-8">
                  <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl" />
                  <div className="relative flex flex-col sm:flex-row sm:items-end gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <GraduationCap className="h-4 w-4 text-emerald-400" />
                        <span className="text-[11px] uppercase tracking-widest text-emerald-400 font-semibold">Mein Lernfortschritt</span>
                      </div>
                      <h1 className="text-3xl lg:text-4xl font-heading font-bold text-white leading-tight">
                        {progress?.read_count
                          ? `Weiter so, ${progress.percent}% geschafft!`
                          : 'Willkommen in deiner Trading-Akademie'}
                      </h1>
                      <p className="text-sm text-zinc-400 mt-2 max-w-xl">
                        {progress?.read_count
                          ? `Du hast ${progress.read_count} von ${progress.total_topics} Themen durchgearbeitet. Bleib dran — jedes gelesene Topic macht dich zum besseren Trader.`
                          : 'Starte mit einem kuratierten Pfad oder stöbere frei in der Sidebar. Premium-Features: KI-Mentor, Live-Recherche, Verständnis-Quiz.'}
                      </p>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      {progress?.streak_days > 0 && (
                        <div className="px-3 py-2 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center gap-2" data-testid="kb-streak">
                          <Flame className="h-4 w-4 text-orange-400" />
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-orange-400/80 leading-none">Streak</p>
                            <p className="text-lg font-bold text-orange-300 leading-tight tabular-nums">{progress.streak_days} {progress.streak_days === 1 ? 'Tag' : 'Tage'}</p>
                          </div>
                        </div>
                      )}
                      <div className="px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-emerald-400" />
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-emerald-400/80 leading-none">Gelesen</p>
                          <p className="text-lg font-bold text-emerald-300 leading-tight tabular-nums">{progress?.read_count ?? 0} / {progress?.total_topics ?? 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="relative mt-6">
                    <div className="h-2 bg-zinc-800/70 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all duration-700 ease-out"
                        style={{ width: `${progress?.percent || 0}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-1.5 tabular-nums">{progress?.percent || 0}% des Gesamtcurriculums</p>
                  </div>
                </div>

                {/* Market Context (Live) */}
                {progress?.market_context && (
                  <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-4 flex items-start gap-3" data-testid="kb-market-context">
                    <div className="shrink-0 w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                      <CalendarClock className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-wider text-red-400 font-semibold">Heute wichtig · High Impact</p>
                      <p className="text-sm text-zinc-100 font-medium mt-0.5">
                        {progress.market_context.event}
                        {progress.market_context.country ? ` · ${progress.market_context.country}` : ''}
                        {progress.market_context.time ? ` · ${progress.market_context.time}` : ''}
                      </p>
                      {progress.market_context.relevant_topic && (
                        <button
                          onClick={() => loadTopic(progress.market_context.relevant_topic.topic_id)}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300"
                        >
                          Passendes Topic: {progress.market_context.relevant_topic.title}
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Continue + Recommended */}
                <div className="grid md:grid-cols-2 gap-4">
                  {progress?.last_read && (
                    <button
                      onClick={() => loadTopic(progress.last_read.topic_id)}
                      data-testid="kb-continue-btn"
                      className="text-left p-5 rounded-xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 to-emerald-700/5 hover:border-emerald-400/50 transition-all group"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Play className="h-4 w-4 text-emerald-400 fill-emerald-400" />
                        <span className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Weiter wo du aufgehört hast</span>
                      </div>
                      <p className="text-lg font-semibold text-white leading-tight group-hover:text-emerald-300 transition-colors">
                        {progress.last_read.title}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">{progress.last_read.part_title} · {progress.last_read.chapter_title}</p>
                    </button>
                  )}

                  {progress?.recommended?.[0] && (
                    <button
                      onClick={() => loadTopic(progress.recommended[0].topic_id)}
                      className="text-left p-5 rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-emerald-500/30 transition-all group"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-4 w-4 text-emerald-400" />
                        <span className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Empfohlen als nächstes</span>
                      </div>
                      <p className="text-lg font-semibold text-white leading-tight group-hover:text-emerald-300 transition-colors">
                        {progress.recommended[0].title}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">{progress.recommended[0].reason}</p>
                    </button>
                  )}
                </div>

                {/* Per-Part Progress */}
                {progress?.per_part?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="h-4 w-4 text-emerald-400" />
                      <h3 className="text-sm font-semibold text-zinc-200">Fortschritt pro Bereich</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {progress.per_part.map(p => {
                        const pct = p.total ? Math.round((p.read / p.total) * 100) : 0;
                        const complete = pct === 100;
                        return (
                          <div
                            key={p.part_id}
                            onClick={() => togglePart(p.part_id)}
                            data-testid={`kb-dashboard-part-${p.part_id}`}
                            className={`group cursor-pointer p-4 rounded-xl border transition-all ${
                              complete
                                ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10'
                                : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'
                            }`}
                          >
                            <div className="flex items-start gap-2 mb-2">
                              <p className="text-sm font-medium text-zinc-200 flex-1 leading-tight group-hover:text-white">{p.title}</p>
                              <span className={`text-xs font-bold tabular-nums shrink-0 ${complete ? 'text-emerald-400' : 'text-zinc-400'}`}>
                                {p.read}/{p.total}
                              </span>
                            </div>
                            <div className="h-1.5 bg-zinc-800/80 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-700 ${complete ? 'bg-emerald-400' : 'bg-gradient-to-r from-emerald-600 to-emerald-400'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <p className={`text-[10px] mt-1 ${complete ? 'text-emerald-400' : 'text-zinc-500'}`}>
                              {complete ? '✓ Abgeschlossen' : `${pct}% geschafft`}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quick-Actions */}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => { togglePart('part_1'); }} className="border-zinc-700" data-testid="kb-start-basics-btn">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Grundlagen starten
                  </Button>
                  <Button onClick={() => setShowChat(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kb-ask-ai-btn">
                    <Sparkles className="h-4 w-4 mr-2" />
                    KI fragen
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* AI Chat Panel */}
          {showChat && (
            <div className="w-[400px] flex flex-col bg-zinc-900/50">
              <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-emerald-400" />
                <span className="text-sm font-semibold">KI-Mentor</span>
                {isTeaser && (
                  <button
                    onClick={(e) => e.preventDefault()}
                    disabled
                    title="Web-Recherche ab 01.05.2026"
                    className="ml-auto flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium bg-zinc-800/60 text-zinc-500 border border-zinc-700 cursor-not-allowed"
                  >
                    <Lock className="h-3 w-3" />
                    Web-Recherche
                  </button>
                )}
                {canUseFullKB && (
                  <button
                    data-testid="kb-web-search-toggle"
                    onClick={() => setWebSearch(v => !v)}
                    title={webSearch ? 'Live Web-Recherche aktiv (Perplexity)' : 'Nur Wissensbasis (GPT-4o)'}
                    className={`ml-auto flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                      webSearch
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-zinc-200'
                    }`}
                  >
                    <Globe className="h-3 w-3" />
                    {webSearch ? 'Web-Recherche an' : 'Web-Recherche'}
                  </button>
                )}
                {!effectiveIsPremium && !isTeaser && <Lock className="h-3 w-3 text-amber-400 ml-auto" />}
              </div>
              
              {isTeaser ? (
                <div className="flex-1 flex items-center justify-center p-6 text-center">
                  <div className="space-y-3 max-w-xs">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                      <Lock className="h-6 w-6 text-emerald-400" />
                    </div>
                    <p className="text-sm font-semibold text-zinc-100">KI-Mentor kommt bald</p>
                    <p className="text-xs text-zinc-500">
                      Der persönliche KI-Coach ist ab <strong className="text-emerald-300">01.05.2026</strong> verfügbar.
                      Live Web-Recherche, Verständnis-Quiz und Textstellen-Erklärungen inklusive.
                    </p>
                  </div>
                </div>
              ) : !effectiveIsPremium ? (
                <div className="flex-1 flex items-center justify-center p-6 text-center">
                  <div className="space-y-3">
                    <Lock className="h-8 w-8 mx-auto text-amber-400" />
                    <p className="text-sm text-muted-foreground">KI-Mentor ist ein Premium Feature</p>
                    <Button size="sm" onClick={() => navigate('/settings')} className="bg-amber-500 hover:bg-amber-600 text-black">
                      Upgrade
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <ScrollArea className="flex-1 p-3">
                    <div className="space-y-3">
                      {chatMessages.length === 0 && (
                        <div className="text-center py-8 space-y-2">
                          <MessageSquare className="h-8 w-8 mx-auto text-zinc-600" />
                          <p className="text-xs text-muted-foreground">Stelle eine Frage zum Trading...</p>
                          <div className="space-y-1 pt-2">
                            {['Was ist ein Fair Value Gap?', 'Erkläre ICT-Konzepte', 'Wie manage ich Risiko?'].map(q => (
                              <button key={q} onClick={() => { setChatInput(q); }}
                                className="block w-full text-left text-xs px-3 py-1.5 rounded bg-zinc-800/50 hover:bg-zinc-800 text-zinc-400 transition-colors">
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {chatMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                            msg.role === 'user' 
                              ? 'bg-emerald-600/20 border border-emerald-500/20 text-zinc-200' 
                              : 'bg-zinc-800/80 border border-zinc-700/50 text-zinc-300'
                          }`}>
                            <div className="whitespace-pre-wrap leading-relaxed">{msg.text}</div>
                            {msg.sources > 0 && (
                              <p className="text-[10px] text-emerald-500 mt-1">{msg.sources} Quellen verwendet</p>
                            )}
                            {msg.sourceList && msg.sourceList.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-zinc-700/50 space-y-1" data-testid="kb-chat-sources">
                                <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">Quellen</p>
                                {msg.sourceList.slice(0, 5).map((s, si) => (
                                  <a key={si} href={s.url} target="_blank" rel="noopener noreferrer"
                                    className="flex items-start gap-1.5 text-[11px] text-emerald-400/90 hover:text-emerald-300 hover:underline leading-tight group">
                                    <span className="text-zinc-500 shrink-0 w-4">[{si + 1}]</span>
                                    <span className="line-clamp-1 flex-1">{s.title || s.url}</span>
                                    <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-0 group-hover:opacity-100 mt-0.5" />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="flex justify-start">
                          <div className="bg-zinc-800/80 border border-zinc-700/50 rounded-xl px-3 py-2">
                            <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                  </ScrollArea>
                  <div className="p-3 border-t border-zinc-800">
                    <div className="flex gap-2">
                      <Input
                        data-testid="kb-chat-input"
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                        placeholder="Frage stellen..."
                        className="bg-zinc-800/50 border-zinc-700 text-sm"
                      />
                      <Button size="icon" onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Floating action menu on text selection */}
      {selectionInfo && canUseFullKB && (
        <div
          style={{
            position: 'fixed',
            left: `${Math.min(Math.max(selectionInfo.x, 130), window.innerWidth - 130)}px`,
            top: `${Math.max(selectionInfo.y - 44, 8)}px`,
            transform: 'translateX(-50%)',
          }}
          className="z-50 flex items-center gap-1 p-1 rounded-full bg-zinc-950 border border-emerald-500/30 shadow-lg shadow-emerald-500/20 animate-in fade-in zoom-in duration-150"
          data-testid="kb-selection-menu"
        >
          <button
            data-testid="kb-action-explain"
            onClick={() => requestExplanation()}
            title="KI-Erklärung anfordern"
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/10 transition"
          >
            <Wand2 className="h-3 w-3" />
            Erklären
          </button>
          <div className="w-px h-4 bg-zinc-800" />
          <button
            data-testid="kb-action-highlight-yellow"
            onClick={() => highlightFromSelection('yellow')}
            title="Gelb markieren"
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-yellow-500/15 transition"
          >
            <span className="w-3.5 h-3.5 rounded-full bg-yellow-400/80 border border-yellow-400" />
          </button>
          <button
            data-testid="kb-action-highlight-emerald"
            onClick={() => highlightFromSelection('emerald')}
            title="Verstanden"
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-emerald-500/15 transition"
          >
            <span className="w-3.5 h-3.5 rounded-full bg-emerald-400/80 border border-emerald-400" />
          </button>
          <button
            data-testid="kb-action-highlight-rose"
            onClick={() => highlightFromSelection('rose')}
            title="Nochmal ansehen"
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-rose-500/15 transition"
          >
            <span className="w-3.5 h-3.5 rounded-full bg-rose-400/80 border border-rose-400" />
          </button>
          <div className="w-px h-4 bg-zinc-800" />
          <button
            data-testid="kb-action-note"
            onClick={openNoteFromSelection}
            title="Notiz hinzufügen"
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-zinc-300 hover:bg-white/5 transition"
          >
            <StickyNote className="h-3 w-3" />
            Notiz
          </button>
        </div>
      )}

      {/* Erklärungs-Modal */}
      {explainOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setExplainOpen(false)}
          data-testid="kb-explain-modal-overlay"
        >
          <div
            className="relative w-full max-w-lg max-h-[80vh] rounded-2xl bg-zinc-950 border border-emerald-500/25 shadow-[0_20px_60px_-10px_rgba(16,185,129,0.3)] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="kb-explain-modal"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Wand2 className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-zinc-100">KI-Erklärung</p>
                  <p className="text-[10px] text-zinc-500">Powered by GPT-4o</p>
                </div>
              </div>
              <button
                onClick={() => setExplainOpen(false)}
                data-testid="kb-explain-close"
                className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 py-3 border-b border-zinc-800 shrink-0 bg-zinc-900/50">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Markierte Stelle</p>
              <p className="text-xs text-zinc-400 italic leading-relaxed line-clamp-3">
                „{explainQuery}"
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {explainLoading ? (
                <div className="flex items-center gap-3 text-zinc-400 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                  KI formuliert die Erklärung…
                </div>
              ) : (
                <div
                  className="prose prose-invert prose-sm max-w-none text-zinc-200 leading-relaxed whitespace-pre-wrap"
                  data-testid="kb-explain-result"
                >
                  {explainResult}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {quizOpen && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
          onClick={() => !quizSubmitting && setQuizOpen(false)}
          data-testid="kb-quiz-overlay"
        >
          <div
            className="relative w-full max-w-2xl max-h-[90vh] rounded-2xl bg-zinc-950 border border-emerald-500/25 shadow-[0_20px_60px_-10px_rgba(16,185,129,0.3)] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="kb-quiz-modal"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Target className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-zinc-100">Verständnis-Quiz</p>
                  <p className="text-[10px] text-zinc-500">{topicContent?.title}</p>
                </div>
              </div>
              <button
                onClick={() => setQuizOpen(false)}
                disabled={quizSubmitting}
                data-testid="kb-quiz-close"
                className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition disabled:opacity-40"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {quizLoading ? (
                <div className="flex flex-col items-center gap-3 py-10 text-zinc-400 text-sm">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
                  KI erstellt dein Verständnis-Quiz…
                </div>
              ) : quizData?.error ? (
                <div className="text-center py-8 text-zinc-400 text-sm">
                  {quizData.error}
                </div>
              ) : quizResult ? (
                // Result view
                <div className="space-y-5" data-testid="kb-quiz-result">
                  <div className={`text-center p-6 rounded-xl border ${
                    quizResult.passed
                      ? 'border-emerald-500/30 bg-emerald-500/5'
                      : 'border-orange-500/30 bg-orange-500/5'
                  }`}>
                    <div className={`w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center ${
                      quizResult.passed ? 'bg-emerald-500/20' : 'bg-orange-500/20'
                    }`}>
                      {quizResult.passed ? (
                        <Trophy className="h-7 w-7 text-emerald-400" />
                      ) : (
                        <Target className="h-7 w-7 text-orange-400" />
                      )}
                    </div>
                    <p className="text-3xl font-bold text-white">{quizResult.correct}/{quizResult.total}</p>
                    <p className={`text-sm mt-1 ${quizResult.passed ? 'text-emerald-400' : 'text-orange-400'}`}>
                      {quizResult.passed
                        ? `Bestanden mit ${quizResult.score_percent}% — Topic als verstanden markiert.`
                        : `${quizResult.score_percent}% — Lies das Topic nochmal und versuche es erneut.`}
                    </p>
                  </div>

                  <div className="space-y-3">
                    {quizData.questions.map((q, qi) => {
                      const r = quizResult.results[qi];
                      const userAns = r?.user_answer;
                      const correctAns = r?.correct_answer;
                      return (
                        <div key={q.id} className={`p-4 rounded-lg border ${
                          r?.is_correct ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-orange-500/20 bg-orange-500/5'
                        }`}>
                          <div className="flex items-start gap-2">
                            {r?.is_correct
                              ? <CircleCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                              : <CircleX className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />}
                            <p className="text-sm font-medium text-zinc-200 flex-1">{q.q}</p>
                          </div>
                          <div className="mt-2 ml-6 space-y-1">
                            {q.options.map((opt, oi) => {
                              const isCorrect = oi === correctAns;
                              const isUserPick = oi === userAns;
                              return (
                                <div key={oi} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${
                                  isCorrect ? 'bg-emerald-500/10 text-emerald-300' :
                                  (isUserPick && !isCorrect) ? 'bg-orange-500/10 text-orange-300 line-through' :
                                  'text-zinc-500'
                                }`}>
                                  <span className="w-4 shrink-0">{isCorrect ? '✓' : (isUserPick ? '✗' : '')}</span>
                                  <span>{opt}</span>
                                </div>
                              );
                            })}
                          </div>
                          {r?.explanation && (
                            <p className="text-xs text-zinc-400 italic mt-2 ml-6 leading-relaxed">
                              💡 {r.explanation}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={retryQuiz}
                      variant="outline"
                      data-testid="kb-quiz-retry"
                      className="flex-1 border-zinc-700"
                    >
                      Neues Quiz
                    </Button>
                    <Button
                      onClick={() => setQuizOpen(false)}
                      data-testid="kb-quiz-done"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                    >
                      Fertig
                    </Button>
                  </div>
                </div>
              ) : quizData?.questions ? (
                // Question view
                <div className="space-y-5">
                  <p className="text-xs text-zinc-500">
                    Beantworte alle {quizData.questions.length} Fragen, um dein Verständnis zu prüfen.
                  </p>
                  {quizData.questions.map((q, qi) => (
                    <div key={q.id} className="space-y-2" data-testid={`kb-quiz-question-${q.id}`}>
                      <p className="text-sm font-medium text-zinc-100">
                        <span className="text-emerald-400 font-semibold mr-2">Frage {qi + 1}.</span>
                        {q.q}
                      </p>
                      <div className="space-y-1.5">
                        {q.options.map((opt, oi) => {
                          const selected = quizAnswers[q.id] === oi;
                          return (
                            <button
                              key={oi}
                              onClick={() => setQuizAnswers(prev => ({ ...prev, [q.id]: oi }))}
                              data-testid={`kb-quiz-q${q.id}-opt${oi}`}
                              className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                                selected
                                  ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-100'
                                  : 'border-zinc-800 bg-zinc-900/40 text-zinc-300 hover:border-zinc-700 hover:bg-zinc-800/50'
                              }`}
                            >
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold mr-2 ${
                                selected ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-500'
                              }`}>
                                {String.fromCharCode(65 + oi)}
                              </span>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            {!quizLoading && !quizResult && quizData?.questions && (
              <div className="px-5 py-3 border-t border-zinc-800 shrink-0 flex items-center justify-between gap-3">
                <p className="text-xs text-zinc-500">
                  {Object.keys(quizAnswers).length} / {quizData.questions.length} beantwortet
                </p>
                <Button
                  onClick={submitQuiz}
                  disabled={Object.keys(quizAnswers).length < quizData.questions.length || quizSubmitting}
                  data-testid="kb-quiz-submit"
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  {quizSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Auswerten
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes Panel (Seitenleiste rechts) */}
      {notesPanelOpen && (
        <div
          className="fixed inset-0 z-[85] flex justify-end bg-black/60 backdrop-blur-sm"
          onClick={() => setNotesPanelOpen(false)}
          data-testid="kb-notes-panel-overlay"
        >
          <div
            className="w-full sm:w-[420px] max-w-full h-full bg-zinc-950 border-l border-zinc-800 overflow-hidden flex flex-col animate-in slide-in-from-right duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="kb-notes-panel"
          >
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-yellow-500/15 border border-yellow-500/30 flex items-center justify-center">
                  <Highlighter className="h-4 w-4 text-yellow-300" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-zinc-100">Meine Notizen</p>
                  <p className="text-[10px] text-zinc-500">{topicContent?.title}</p>
                </div>
              </div>
              <button
                onClick={() => setNotesPanelOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {highlights.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Highlighter className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Noch keine Markierungen.</p>
                  <p className="text-xs mt-1">Markiere eine Textstelle und wähle eine Farbe.</p>
                </div>
              ) : (
                highlights.map(h => (
                  <div
                    key={h.highlight_id}
                    className="p-3 rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 transition"
                    data-testid={`kb-note-item-${h.highlight_id}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <span className={`w-3 h-3 rounded-full mt-0.5 shrink-0 ${
                        h.color === 'emerald' ? 'bg-emerald-400' :
                        h.color === 'rose' ? 'bg-rose-400' : 'bg-yellow-400'
                      }`} />
                      <p className="text-xs text-zinc-300 italic leading-relaxed flex-1 line-clamp-3">„{h.text}"</p>
                      <button
                        onClick={() => deleteHighlight(h.highlight_id)}
                        title="Löschen"
                        className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-zinc-600 hover:text-rose-400 hover:bg-rose-500/10 transition"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    {h.note ? (
                      <div
                        onClick={() => openNoteEditor(h)}
                        className="cursor-pointer px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 hover:border-yellow-500/30 transition"
                      >
                        <p className="text-[10px] uppercase tracking-wider text-yellow-500/80 font-semibold mb-1 flex items-center gap-1">
                          <StickyNote className="h-3 w-3" />
                          Notiz
                        </p>
                        <p className="text-xs text-zinc-200 leading-relaxed whitespace-pre-wrap">{h.note}</p>
                      </div>
                    ) : (
                      <button
                        onClick={() => openNoteEditor(h)}
                        className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                      >
                        <StickyNote className="h-3 w-3" />
                        Notiz hinzufügen
                      </button>
                    )}
                    <div className="flex items-center gap-1 mt-2">
                      {['yellow', 'emerald', 'rose'].map(c => (
                        <button
                          key={c}
                          onClick={() => updateHighlight(h.highlight_id, { color: c })}
                          className={`w-4 h-4 rounded-full transition ${
                            h.color === c ? 'ring-2 ring-offset-2 ring-offset-zinc-900 ring-white/40 scale-110' : 'opacity-60 hover:opacity-100'
                          } ${
                            c === 'emerald' ? 'bg-emerald-400' :
                            c === 'rose' ? 'bg-rose-400' : 'bg-yellow-400'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Note Editor Modal */}
      {noteModalOpen && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setNoteModalOpen(false)}
          data-testid="kb-note-modal-overlay"
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-zinc-950 border border-yellow-500/25 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="kb-note-modal"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <StickyNote className="h-4 w-4 text-yellow-300" />
                <p className="text-sm font-semibold text-zinc-100">
                  {noteEditingHl ? 'Notiz bearbeiten' : 'Neue Notiz'}
                </p>
              </div>
              <button
                onClick={() => setNoteModalOpen(false)}
                className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-200 hover:bg-white/5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="px-3 py-2 rounded-lg bg-yellow-400/10 border border-yellow-500/20">
                <p className="text-[10px] uppercase tracking-wider text-yellow-500/80 font-semibold mb-1">Markierter Text</p>
                <p className="text-xs text-zinc-300 italic line-clamp-3">
                  „{noteEditingHl?.text || notePendingText}"
                </p>
              </div>
              <textarea
                data-testid="kb-note-textarea"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Deine persönliche Notiz zu dieser Stelle…"
                autoFocus
                rows={5}
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 focus:border-yellow-500/50 focus:outline-none text-sm text-zinc-100 placeholder:text-zinc-600 resize-none"
              />
            </div>
            <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setNoteModalOpen(false)}
                className="border-zinc-700"
              >
                Abbrechen
              </Button>
              <Button
                data-testid="kb-note-save-btn"
                onClick={saveNote}
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
              >
                Speichern
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePage;
